import { useState } from 'react'

import {
  CreateTrainingsScormPackageDocument,
  CreateTrainingsScormPackageMutation,
  TrainingContentRootDocument,
  TrainingDetailRouteDocument,
} from 'generated/resources/graphql'

import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'

const MAX_FILE_SIZE_BYTES = 500 * 1024 * 1024

const ACCEPTED_EXTENSIONS = ['.zip']

export type ScormUploadState = 'idle' | 'uploading' | 'error'

interface UseScormPackageUploadParams {
  trainingId: number
  companyId: number
  onSuccess?: () => void
}

export const useScormPackageUpload = ({
  trainingId,
  companyId,
  onSuccess,
}: UseScormPackageUploadParams) => {
  const [uploadState, setUploadState] = useState<ScormUploadState>('idle')
  const [error, setError] = useState<string | null>(null)

  const [createScormPackage, { loading: uploading }] = useMutation(
    CreateTrainingsScormPackageDocument,
    {
      extractorFn: (data: CreateTrainingsScormPackageMutation) =>
        data.trainingsMutations.createTrainingsScormPackage,
      refetchQueries: [TrainingContentRootDocument, TrainingDetailRouteDocument],
    }
  )

  const validateFile = (file: File): string | null => {
    const lastDotIndex = file.name.lastIndexOf('.')
    const extension = lastDotIndex !== -1 ? file.name.toLowerCase().slice(lastDotIndex) : ''

    if (!ACCEPTED_EXTENSIONS.includes(extension)) {
      return i18n.t('trainings.scorm.upload.invalid_file')
    }
    if (file.size > MAX_FILE_SIZE_BYTES) {
      return i18n.t('trainings.scorm.upload.file_too_large')
    }
    return null
  }

  const upload = async (file: File) => {
    const validationError = validateFile(file)
    if (validationError) {
      setError(validationError)
      setUploadState('error')
      return
    }

    setError(null)
    setUploadState('uploading')

    const result = await createScormPackage({
      variables: {
        trainingId,
        companyId,
        file,
      },
    })

    const handled = handleResult(result, () => {
      setError(i18n.t('trainings.scorm.upload.error'))
      setUploadState('error')
    })

    if (handled.success && handled.data.scormPackage) {
      setUploadState('idle')
      onSuccess?.()
    } else if (!handled.success) {
      const firstError = handled.errors[0]
      const errorMessage =
        firstError && 'message' in firstError
          ? firstError.message
          : i18n.t('trainings.scorm.upload.error')
      setError(errorMessage)
      setUploadState('error')
    }
  }

  const reset = () => {
    setError(null)
    setUploadState('idle')
  }

  return {
    upload,
    uploading,
    uploadState,
    error,
    reset,
  }
}
